import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AgentConfig, PersonaConfig, QueryType } from "../types";
import { useSession } from "../context/SessionContext";
import { useAnalytics } from "../context/AnalyticsContext";
import { getVertical, DemoCustomer, TestHints, VerticalConfig } from "../data/verticals";
import { TscCustomerPicker } from "../tsc/TscCustomerPicker";
import { isTscSlug, tscDemoForSlug } from "../tsc/tscDemos";

const uuidv4 = () => crypto.randomUUID();

const usd = (n: number) =>
  `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TIER_COLORS: Record<string, { text: string; bg: string }> = {
  Bronze:   { text: "var(--nx-peach-text)",  bg: "rgba(253,186,116,0.10)" },
  Silver:   { text: "var(--nx-slate-text)",  bg: "rgba(148,163,184,0.12)" },
  Gold:     { text: "var(--nx-warn-text)",   bg: "rgba(251,191,36,0.10)"  },
  Platinum: { text: "var(--nx-violet-text)", bg: "rgba(167,139,250,0.10)" },
};

// ─── Use-case chips ──────────────────────────────────────────────────────────

const CHIPS: { id: QueryType; label: string; color: string; bg: string; border: string; icon: React.ReactNode }[] = [
  {
    id: "wismo",
    label: "Where's my order?",
    color: "var(--nx-blue-text)",
    bg: "rgba(59,130,246,0.10)",
    border: "rgba(59,130,246,0.22)",
    icon: (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" />
      </svg>
    ),
  },
  {
    id: "cancellation",
    label: "Cancel or return",
    color: "var(--nx-rose-text)",
    bg: "rgba(244,63,94,0.10)",
    border: "rgba(244,63,94,0.22)",
    icon: (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
      </svg>
    ),
  },
  {
    id: "store-desk",
    label: "Store support",
    color: "var(--nx-green-text)",
    bg: "rgba(52,211,153,0.10)",
    border: "rgba(52,211,153,0.22)",
    icon: (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
  {
    id: "product-rec",
    label: "Product & pricing",
    color: "var(--nx-violet-text)",
    bg: "rgba(167,139,250,0.10)",
    border: "rgba(167,139,250,0.22)",
    icon: (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" />
      </svg>
    ),
  },
];

// ─── Customer card (CRM details + what you can test) ──────────────────────────

function CustomerCard({ c, ht, vertical, onSelect }: { c: DemoCustomer; ht: TestHints; vertical: VerticalConfig; onSelect: () => void }) {
  const tier = TIER_COLORS[c.loyaltyTier] ?? { text: "var(--nx-text-2)", bg: "var(--nx-hover)" };
  const initials = ((c.firstName[0] ?? "") + (c.lastName[0] ?? "")).toUpperCase() || (c.name[0] ?? "?").toUpperCase();

  // Fulfillment: store pickup vs delivery, derived from the order statuses.
  const pickup = new Set(["ready_for_pickup", "pickup_processing"]);
  const kinds = new Set((c.orders ?? []).map((o) => (pickup.has(o.status) ? "pickup" : "delivery")));
  const fulfillment = kinds.size === 0 ? "" : kinds.size > 1 ? "Mixed" : kinds.has("pickup") ? "Store pickup" : "Delivery";
  const productNames = (c.orders ?? []).map((o) => o.product).filter(Boolean).join(", ");
  const orderIds = (c.orders ?? []).map((o) => o.orderId).filter(Boolean).join(", ");
  const ordersCount = c.ordersCount ?? c.orders?.length ?? 0;

  // A vertical can supply its own rows; the retail set below is the default.
  const fields: { label: string; value: string; accent?: boolean }[] = vertical.cardFields
    ? vertical.cardFields(c)
    : [
        { label: "Email", value: c.email },
        { label: "Phone", value: c.phone },
        { label: "Product", value: productNames },
        { label: "Order ID", value: orderIds },
        { label: "Orders", value: `${ordersCount} order${ordersCount === 1 ? "" : "s"}` },
        { label: "Fulfillment", value: fulfillment },
      ].filter((f) => f.value);

  return (
    <button
      type="button"
      onClick={onSelect}
      className="group w-full h-full flex flex-col text-left rounded-2xl overflow-hidden transition-all duration-150 active:scale-[0.99]"
      style={{ background: "var(--nx-card-bg)", border: "1px solid var(--nx-card-border)" }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(37,130,235,0.30)";
        (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 8px 36px rgba(0,0,0,0.30)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--nx-card-border)";
        (e.currentTarget as HTMLButtonElement).style.boxShadow = "none";
      }}
    >
      {/* Header: avatar · name · tier */}
      <div className="flex items-center gap-3.5 px-5 pt-4 pb-4" style={{ borderBottom: "1px solid var(--nx-border)" }}>
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-[13px]"
          style={{ background: "#F9F9F9", color: "#525252" }}
        >
          {initials}
        </div>
        {/* The name gets the row to itself — the badges wrap under it rather than
            squeezing it into an ellipsis, which made every card read "Dana Whi…". */}
        {/* Name, then badges on their own row, then a two-line note. Fixed row count
            keeps every card header the same height so the detail grids line up. */}
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-semibold truncate" style={{ color: "var(--nx-text-1)" }}>{c.name}</div>
          <div className="flex items-center gap-1.5 mt-1">
            {c.loyaltyTier && (
              <span
                className="flex-shrink-0 text-[10px] font-semibold px-2.5 py-0.5 rounded-full"
                style={{ background: tier.bg, color: tier.text }}
              >
                {c.loyaltyTier}
              </span>
            )}
            {ht.featured && (
              <span className="flex-shrink-0 text-[9px] font-bold uppercase tracking-[0.04em] px-2 py-0.5 rounded-full" style={{ background: "rgba(251,191,36,0.14)", border: "1px solid rgba(251,191,36,0.30)", color: "var(--nx-warn-text)" }}>★ {ht.featured}</span>
            )}
          </div>
          {c.crmNotes && (
            <div className="text-[12px] mt-1.5 line-clamp-2" style={{ color: "var(--nx-text-3)" }}>{c.crmNotes}</div>
          )}
        </div>
      </div>

      {/* Detail grid — two columns, label over value */}
      <div className="px-5 py-4 grid grid-cols-2 gap-x-5 gap-y-3.5">
        {fields.map((f) => (
          <div key={f.label} className="flex flex-col gap-0.5 min-w-0">
            <span className="text-[10px] font-semibold uppercase tracking-[0.06em]" style={{ color: "var(--nx-text-3)" }}>
              {f.label}
            </span>
            <span
              className="text-[13px] font-medium truncate tabular-nums"
              style={{ color: f.accent ? "var(--nx-accent-text)" : "var(--nx-text-1)" }}
            >
              {f.value}
            </span>
          </div>
        ))}
      </div>

      {/* What you can test */}
      <div className="px-5 pt-3.5 pb-4 mt-auto" style={{ borderTop: "1px solid var(--nx-border)" }}>
        <div className="text-[10px] font-semibold uppercase tracking-[0.06em] mb-2" style={{ color: ht.featured ? "var(--nx-warn-text)" : "var(--nx-accent-text)" }}>
          {ht.featured ? "★ Scripted demo" : "What you can test"}
        </div>
        <ul className="space-y-1">
          {ht.hints.map((h, i) => (
            <li key={i} className="flex gap-2 text-[12px] leading-snug" style={{ color: "var(--nx-text-2)" }}>
              <span aria-hidden style={{ color: ht.featured ? "var(--nx-warn-text)" : "var(--nx-accent-text)" }}>•</span>
              <span>{h}</span>
            </li>
          ))}
        </ul>
      </div>
    </button>
  );
}

// ─── Stage A — customer list ─────────────────────────────────────────────────

function CustomerListStage({ vertical, onSelect }: { vertical: VerticalConfig; onSelect: (c: DemoCustomer) => void }) {
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();
  const filtered = useMemo(() => {
    const base = query
      ? vertical.customers.filter(
          (c) => c.name.toLowerCase().includes(query) || c.email.toLowerCase().includes(query),
        )
      : vertical.customers;
    // Surface the richest profiles first: featured demos, then multi-order customers,
    // so double/triple-order profiles are visible without scrolling.
    const rank = (c: DemoCustomer) =>
      vertical.testHints(c).featured ? 0 : 10 - Math.min(c.ordersCount ?? c.orders?.length ?? 0, 5);
    return [...base].sort((a, b) => rank(a) - rank(b));
  }, [query, vertical]);

  return (
    <div className="step-enter w-full">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-semibold tracking-[0.10em] uppercase mb-5"
            style={{ background: "rgba(37,130,235,0.12)", border: "1px solid rgba(37,130,235,0.22)", color: "var(--nx-accent-text)" }}
          >
            <svg width="7" height="7" viewBox="0 0 8 8" fill="none"><circle cx="4" cy="4" r="3" fill="currentColor" /></svg>
            {vertical.crmLabel}
          </div>
          <h1 className="text-[34px] font-bold tracking-[-0.03em] leading-tight mb-3" style={{ color: "var(--nx-text-1)" }}>
            Pick a customer
          </h1>
          <p className="text-[15px] leading-relaxed" style={{ color: "var(--nx-text-2)" }}>
            {vertical.pickerBlurb}
          </p>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <svg
            className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none"
            width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="var(--nx-text-3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email…"
            spellCheck={false}
            className="w-full h-11 pl-10 pr-4 rounded-xl text-[14px] outline-none transition-colors"
            style={{ background: "var(--nx-card-bg)", border: "1px solid var(--nx-card-border)", color: "var(--nx-text-1)" }}
            onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(37,130,235,0.45)")}
            onBlur={(e) => (e.currentTarget.style.borderColor = "var(--nx-card-border)")}
          />
        </div>

        <div className="text-[12px] mb-4 px-1" style={{ color: "var(--nx-text-3)" }}>
          {filtered.length} customer{filtered.length === 1 ? "" : "s"}{query ? ` matching “${q.trim()}”` : ""}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="max-w-2xl mx-auto rounded-2xl py-12 text-center" style={{ background: "var(--nx-card-bg)", border: "1px solid var(--nx-card-border)" }}>
          <p className="text-[14px] font-medium" style={{ color: "var(--nx-text-1)" }}>No customers match “{q.trim()}”</p>
          <p className="text-[12px] mt-1" style={{ color: "var(--nx-text-3)" }}>Try a different name or email.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((c) => (
            <CustomerCard key={c.id} c={c} ht={vertical.testHints(c)} vertical={vertical} onSelect={() => onSelect(c)} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Stage B — pick a use case (kept for completeness; the picker deep-links a query) ──

function UseCaseStage({ persona, onPick, onBack }: { persona: PersonaConfig; onPick: (q: QueryType) => void; onBack: () => void }) {
  const tier = TIER_COLORS[persona.loyaltyTier] ?? { text: "var(--nx-text-2)", bg: "transparent" };

  return (
    <div className="step-enter w-full">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 mb-5 text-[12px] transition-colors"
        style={{ color: "var(--nx-text-3)" }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "var(--nx-text-1)")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "var(--nx-text-3)")}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Pick a different customer
      </button>

      <div
        className="flex items-center gap-3.5 rounded-2xl p-4 mb-7"
        style={{ background: "var(--nx-card-bg)", border: "1px solid var(--nx-card-border)" }}
      >
        <div
          className={`w-11 h-11 rounded-full bg-gradient-to-br ${persona.avatarBg} flex items-center justify-center text-white font-bold text-[13px] flex-shrink-0`}
          style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.35)" }}
        >
          {persona.avatarInitials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-semibold" style={{ color: "var(--nx-text-1)" }}>{persona.name}</div>
          <div className="text-[11px] mt-0.5 truncate" style={{ color: "var(--nx-text-3)" }}>
            {persona.email ? `${persona.email} · ` : ""}{persona.accountId}
            {persona.totalSpentUsd != null ? ` · ${usd(persona.totalSpentUsd)} spent` : ""}
          </div>
        </div>
        <span className="flex-shrink-0 text-[10px] font-semibold px-2.5 py-1 rounded-full"
          style={{ background: tier.bg, color: tier.text }}>
          {persona.loyaltyTier}
        </span>
      </div>

      <div className="text-center mb-6">
        <h2 className="text-[22px] font-bold tracking-[-0.02em] mb-2" style={{ color: "var(--nx-text-1)" }}>
          What can the agent help with?
        </h2>
        <p className="text-[13px]" style={{ color: "var(--nx-text-3)" }}>
          Pick a use case to start the demo call.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 justify-center">
        {CHIPS.map((chip) => (
          <button
            key={chip.id}
            type="button"
            onClick={() => onPick(chip.id)}
            className="flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[12px] font-medium transition-all duration-150 active:scale-95"
            style={{ background: "transparent", border: "1px solid var(--nx-border)", color: "var(--nx-text-3)" }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = chip.border;
              (e.currentTarget as HTMLButtonElement).style.color       = chip.color;
              (e.currentTarget as HTMLButtonElement).style.background  = chip.bg;
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--nx-border)";
              (e.currentTarget as HTMLButtonElement).style.color       = "var(--nx-text-3)";
              (e.currentTarget as HTMLButtonElement).style.background  = "transparent";
            }}
          >
            {chip.icon}
            {chip.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

interface Props {
  persona:   PersonaConfig | undefined;
  queryType: QueryType | null;
  agent?:    AgentConfig;
}

export function QuerySelectionStep({ persona, queryType, agent }: Props) {
  const navigate = useNavigate();
  const { dispatch } = useSession();
  const { track } = useAnalytics();

  const vertical = getVertical(agent?.slug);
  const agentSlug = agent?.slug ?? vertical.slug;

  useEffect(() => {
    track("experience.view");
  }, [track]);

  const handleCustomerSelect = useCallback((c: DemoCustomer) => {
    const personaId = `${vertical.personaPrefix}${c.id}`;
    const query = vertical.queryForCustomer(c);
    dispatch({ type: "INIT_SESSION", agentSlug, sessionId: uuidv4() });
    dispatch({ type: "SET_PERSONA", personaId });
    track("experience.persona_selected", { persona: personaId, source: vertical.slug });
    navigate(`/nurix-experience/experience?agent=${agentSlug}&persona=${personaId}&query=${query}&channel=web`);
  }, [vertical, agentSlug, dispatch, navigate, track]);

  const handleQueryPick = useCallback((qt: QueryType) => {
    if (!persona) return;
    dispatch({ type: "SET_QUERY", queryType: qt });
    track("experience.query_selected", { query: qt, persona: persona.id });
    navigate(`/nurix-experience/experience?agent=${agentSlug}&persona=${persona.id}&query=${qt}`);
  }, [persona, agentSlug, dispatch, navigate, track]);

  const handleBackToCustomers = useCallback(() => {
    navigate(`/nurix-experience/experience?agent=${agentSlug}`);
  }, [navigate, agentSlug]);

  // Tractor Supply has its own picker in the brand's look, grouped primary → additional.
  if (isTscSlug(vertical.slug) && !persona) {
    return <TscCustomerPicker demo={tscDemoForSlug(vertical.slug)} onSelect={(c) => handleCustomerSelect(c as unknown as DemoCustomer)} />;
  }

  return (
    <div className="step-enter min-h-screen flex flex-col items-center px-6 py-12 relative" style={{ background: "var(--nx-bg)" }}>
      <div className="orb" style={{ width: 600, height: 600, top: "-15%", right: "-10%", background: "radial-gradient(circle, rgba(37,130,235,0.08) 0%, transparent 65%)" }} />
      <div className="orb" style={{ width: 400, height: 400, bottom: "-5%", left: "-8%", background: "radial-gradient(circle, rgba(167,139,250,0.06) 0%, transparent 65%)" }} />

      <div className="w-full max-w-7xl relative">
        {!persona && <CustomerListStage vertical={vertical} onSelect={handleCustomerSelect} />}
        {persona && !queryType && <UseCaseStage persona={persona} onPick={handleQueryPick} onBack={handleBackToCustomers} />}
      </div>

      <p className="mt-auto pt-10 text-[12px] text-center" style={{ color: "var(--nx-text-3)" }}>
        Powered by NuPlay AI · No account required
      </p>
    </div>
  );
}
